/**
 * @fileoverview Enforce artwork customization and regulated hazard palettes.
 * @module ui-artwork/colorPolicy
 *
 * Responsibilities: Validate requested colors against catalog entry policy.
 * Dependencies: ArtworkCatalogEntry, ApplyColorPolicyResult.
 * Architecture: Pure color-policy domain function.
 * Notes: Controlled palettes are compared case-insensitively.
 */
import type { ApplyColorPolicyResult, ArtworkCatalogEntry } from './types';

const isHexColor = (color: string): boolean => /^#(?:[\da-f]{3}|[\da-f]{6})$/i.test(color);

/** @reusable applyColorPolicy */
export function applyColorPolicy(entry: ArtworkCatalogEntry, requestedColor?: string | null): ApplyColorPolicyResult {
  const requested = requestedColor?.trim();
  if (entry.colorPolicy === 'fixed') {
    return { color: entry.defaultColor, isLocked: true, rejected: !!requested && requested.toLowerCase() !== entry.defaultColor.toLowerCase() };
  }
  if (entry.colorPolicy === 'controlled-hazard') {
    const allowed = entry.allowedHazardColors ?? [];
    const match = requested ? allowed.find((candidate) => candidate.hex.toLowerCase() === requested.toLowerCase()) : undefined;
    return {
      color: match?.hex ?? entry.defaultColor,
      isLocked: true,
      rejected: !!requested && !match,
    };
  }
  return {
    color: requested && isHexColor(requested) ? requested : entry.defaultColor,
    isLocked: false,
    rejected: !!requested && !isHexColor(requested),
  };
}
